import {
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType
} from '@/types/transcriptionSessionTypes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export interface TranscriptionSessionJobType {
  Upload: 0;
  Transcribe: 1;
  Process: 2;
  Analyze: 3;
  Summarize: 4;
  Other: 99;
}

export interface TranscriptionSessionJobStatus {
  Queued: 0;
  Running: 1;
  Succeeded: 2;
  Failed: 3;
  Cancelled: 4;
}

export interface TranscriptionSessionJob {
  id: string;
  sessionId: string;
  jobType: number | string; // Accept string enum from server
  status: number | string; // Accept string enum from server
  progressPercent?: number | null;
  message?: string | null;
  detail?: string | null;
  correlationId?: string | null;
  startedAtUtc: string;
  completedAtUtc?: string | null;
}

export interface SessionJobUpdate {
  event: string; // e.g., "jobAdded", "jobUpdated"
  job: TranscriptionSessionJob;
}

/**
 * Helper function to acquire Auth0 access token.
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn(
      'Silent token acquisition failed for TranscriptionSessionJobService:',
      error
    );
    try {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) throw new Error('Access token is null or undefined.');
      return token;
    } catch (popupError) {
      console.error(
        'Popup token acquisition failed for TranscriptionSessionJobService:',
        popupError
      );
      throw new Error(`Failed to get access token: ${popupError}`);
    }
  }
};

/**
 * Get all jobs for a transcription session
 */
export const getSessionJobs = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TranscriptionSessionJob[]> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(
    `${API_BASE_URL}/transcriptionsessions/${sessionId}/jobs`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-User-Id': userId
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch session jobs: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

/**
 * Subscribe to session job updates via Server-Sent Events
 */
export const subscribeToSessionJobs = (
  sessionId: string,
  userId: string,
  accessToken: string,
  onUpdate: (update: SessionJobUpdate) => void,
  onError: (error: Error) => void
): (() => void) => {
  // EventSource doesn't support custom headers in all browsers
  // Pass auth via query parameters instead
  const url = new URL(
    `${API_BASE_URL}/transcriptionsessions/${sessionId}/jobs/stream`
  );
  url.searchParams.set('access_token', accessToken);
  url.searchParams.set('user_id', userId);

  const fullUrl = url.toString();
  const eventSource = new EventSource(fullUrl);

  eventSource.onopen = () => {
    // connection opened
  };

  eventSource.onmessage = (event) => {
    try {
      const update: SessionJobUpdate = JSON.parse(event.data);
      onUpdate(update);
    } catch (error) {
      console.error('Failed to parse job update:', error);
      onError(new Error('Failed to parse job update'));
    }
  };

  eventSource.onerror = (event) => {
    console.error('SessionJobs SSE error', event);
    onError(new Error('Connection to job updates failed'));
  };

  // Return cleanup function
  return () => {
    eventSource.close();
  };
};

/**
 * Get the display name for a job type
 */
export const getJobTypeName = (jobType: number | string): string => {
  // Handle server string enums directly
  if (typeof jobType === 'string') {
    const s = jobType.trim().toLowerCase();
    if (s === 'upload') return 'Upload';
    if (s === 'transcribe') return 'Transcribe';
    if (s === 'process') return 'Process';
    if (s === 'analyze') return 'Analyze';
    if (s === 'summarize') return 'Summarize';
    if (s === 'other') return 'Other';
    return jobType;
  }
  // Fallback: numeric enum
  switch (jobType) {
    case 0:
      return 'Upload';
    case 1:
      return 'Transcribe';
    case 2:
      return 'Process';
    case 3:
      return 'Analyze';
    case 4:
      return 'Summarize';
    case 99:
      return 'Other';
    default:
      return 'Unknown';
  }
};

/**
 * Get the display name for a job status
 */
export const getJobStatusName = (status: number | string): string => {
  if (typeof status === 'string') {
    const s = status.trim().toLowerCase();
    if (s === 'queued') return 'Queued';
    if (s === 'running') return 'Running';
    if (s === 'succeeded') return 'Succeeded';
    if (s === 'failed') return 'Failed';
    if (s === 'cancelled') return 'Cancelled';
    return status;
  }
  switch (status) {
    case 0:
      return 'Queued';
    case 1:
      return 'Running';
    case 2:
      return 'Succeeded';
    case 3:
      return 'Failed';
    case 4:
      return 'Cancelled';
    default:
      return 'Unknown';
  }
};
